import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { Observable, of, Subscription, throttleTime } from 'rxjs';
import { CommentRecording } from 'src/app/pages/virtual-interview/models/virtual-interview-model';
import { InterviewService } from 'src/app/pages/virtual-interview/services/interview.service';
import { VideoRecoderService } from 'src/app/services/video-recorder/video-recoder.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { CompanyVrServiceService } from '../../service/company-vr-service.service';

@Component({
  selector: 'app-session-question',
  templateUrl: './session-question.component.html',
  styleUrls: ['./session-question.component.css'],
})
export class SessionQuestionComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() question: any;
  @Input() isLastQuestion: boolean = false;
  @Input() sessionId: string = '';
  @Input() language: string = 'en';
  @Output() next = new EventEmitter<any>();

  // comment outputs (mirrors interview-question)
  @Input() currentQuestionComments: CommentRecording[] = [];
  @Input() commentsRemaining: number = 0;
  @Input() isCommentRecording: boolean = false;
  @Input() commentTimeRemaining: number = 30;

  @Output() commentAdded = new EventEmitter<void>();

  @ViewChild('previewVideo') previewVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('recordedVideo') recordedVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('commentPreviewVideo') commentPreviewVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('explainAudio') explainAudio!: ElementRef<HTMLAudioElement>;
  @ViewChild('questionVideo') questionVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('questionAudio') questionAudio!: ElementRef<HTMLAudioElement>;

  bars: number[] = Array(32).fill(4);
  commentBars: number[] = Array(32).fill(4);
  isRecording$ = this.recorderService.isRecording$;
  isVideoOn = false;
  isMicOn = false;
  showRecordedVideo = false;
  startFlag = false;
  isTimeUp = false;

  canRecord = true;
  isQuestionCompleted = false;
  canUseComment = false;

  isLoadingExplanation = false;
  explainText: string = '';
  explainAudioUrl: string = '';
  isPlayingExplanation = false;

  questionAudioUrl: string = '';
  isPlayingQuestionAudio = false;

  currentTime = 0;
  maxTime = 60000;
  private speakingTimer: any = null;

  activeQuestion: string = '';
  showExplanation = false;
  hasExplanation = false;
  questionVideoUrl: string = '';
  isPlayingQuestionVideo = false;

  // Comment recording state
  commentRecordVisibilty = false;
  commentElapsedTime: number = 0;
  commentFinalTime: number = 0;
  currentCommentNumber: number = 0;
  pendingComment: CommentRecording | null = null;
  private isRecordingComment = false;
  private commentChunkIndex: number = 0;
  private commentChunkUploadQueue: Array<{
    chunk: Blob;
    cumulativeDuration: number;
    chunkIndex: number;
  }> = [];
  private commentChunkUploadInFlight = false;
  private commentStopRequested = false;
  private commentRecordingStopped = false;
  private lastCommentChunkTime: number = 0;
  private commentChunkUploadSubscription: Subscription | null = null;

  private currentVideoBlob: Blob | null = null;
  private currentVideoUrl: string | null = null;
  private recordedVideoId: string | null = null;
  private chunkUploadSubscription: Subscription | null = null;
  private lastChunkTime: number = 0;

  private subs: Subscription[] = [];

  constructor(
    public recorderService: VideoRecoderService,
    private zone: NgZone,
    private interviewService: InterviewService,
    private companyVrService: CompanyVrServiceService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    if (this.question?.remainingAdditionalAttempts !== undefined) {
      this.commentsRemaining = this.question.remainingAdditionalAttempts;
    }
    this.setupSubscriptions();
    this.updateBarsByScreenSize();
  }

  ngAfterViewInit() {
    this.subs.push(
      this.recorderService.previewStream$.subscribe((stream) => {
        if (this.commentRecordVisibilty && this.commentPreviewVideo) {
          this.commentPreviewVideo.nativeElement.srcObject = stream ?? null;
        } else if (this.previewVideo) {
          this.previewVideo.nativeElement.srcObject = stream ?? null;
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['question'] && changes['question'].currentValue) {
      this.syncQuestionDuration();
      this.activeQuestion =
        this.question.text || this.question.interviewVideo?.questionText || '';

      if (this.question.remainingAdditionalAttempts !== undefined) {
        this.commentsRemaining = this.question.remainingAdditionalAttempts;
      }

      this.checkQuestionStatus();

      this.hasExplanation = this.question.explained === false;

      this.showExplanation = false;
      this.explainText = '';
      this.explainAudioUrl = '';
      this.isPlayingExplanation = false;

      this.commentElapsedTime = 0;
      this.commentFinalTime = 0;
      this.commentRecordVisibilty = false;
      this.pendingComment = null;
      this.currentCommentNumber = 0;
      this.commentChunkIndex = 0;

      if (this.question.interviewVideo?.url) {
        const rawUrl = this.question.interviewVideo.url;
        const fixedUrl = rawUrl.replace(/\\/g, '/');
        this.questionAudioUrl = environment.apiUrl + fixedUrl;

        setTimeout(() => {
          this.playQuestionAudio();
        }, 500);
      }
    }
  }

  private syncQuestionDuration() {
    const durationInSeconds = Number(this.question?.interviewVideo?.duration);
    const fallbackDurationInSeconds = 60;
    const resolvedDurationInSeconds =
      Number.isFinite(durationInSeconds) && durationInSeconds > 0
        ? durationInSeconds
        : fallbackDurationInSeconds;

    this.maxTime = resolvedDurationInSeconds * 1000;
    this.currentTime = Math.min(this.currentTime, this.maxTime);
  }

  isRTL(): boolean {
    return this.language === 'ar';
  }

  private checkQuestionStatus() {
    const recordedVideos = this.question.recordedVideos;

    if (!recordedVideos) {
      this.canRecord = true;
      this.isQuestionCompleted = false;
      this.canUseComment = false;
      this.recordedVideoId = null;
      return;
    }

    this.recordedVideoId = recordedVideos.recordedVideoId;

    if (
      recordedVideos.status === 'COMPLETED' ||
      recordedVideos.status === 'RECORDING' ||
      recordedVideos.status === 'RECORDED'
    ) {
      this.canRecord = false;
      this.isQuestionCompleted = true;
      this.startFlag = true;

      this.canUseComment = !recordedVideos.usedAdditional && this.commentsRemaining > 0;

      if (recordedVideos.url) {
        this.loadRecordedVideo(recordedVideos.url);
      }
      return;
    }

    if (recordedVideos.status === 'IDLE') {
      this.canRecord = true;
      this.isQuestionCompleted = false;
      this.canUseComment = false;

      if (recordedVideos.url) {
        this.loadRecordedVideo(recordedVideos.url);
      }

      if (recordedVideos.duration && recordedVideos.duration > 0) {
        this.currentTime = recordedVideos.duration * 1000;
        this.startFlag = true;
      }
    }
  }

  private loadRecordedVideo(videoPath: string) {
    const fixedUrl = videoPath.replace(/\\/g, '/');
    const fullUrl = environment.apiUrl + fixedUrl;

    this.zone.run(() => {
      this.currentVideoUrl = fullUrl;
      this.showRecordedVideo = true;

      setTimeout(() => {
        if (this.recordedVideo) {
          const video = this.recordedVideo.nativeElement;
          video.src = fullUrl;
          video.load();
        }
      }, 100);
    });
  }

  private updateBarsByScreenSize() {
    const isMobile = window.innerWidth < 600;
    const barCount = isMobile ? 24 : 32;
    this.bars = Array(barCount).fill(4);
  }

  private setupSubscriptions() {
    this.subs.push(
      this.recorderService.volumeLevel$
        .pipe(throttleTime(100))
        .subscribe((volume) => {
          if (this.isRecording$.value || this.isMicOn) {
            this.updateWaveform(volume);
          } else {
            this.resetWaveform();
          }
        }),
    );

    this.subs.push(
      this.recorderService.commentVolumeLevel$
        .pipe(throttleTime(100))
        .subscribe((volume) => {
          if (this.isCommentRecording) {
            this.updateCommentWaveform(volume);
          } else {
            this.resetCommentWaveform();
          }
        }),
    );

    this.subs.push(
      this.recorderService.recordedBlob$.subscribe((blob) => {
        if (!blob) return;

        this.zone.run(() => {
          this.currentVideoBlob = blob;

          if (
            !this.currentVideoUrl ||
            !this.currentVideoUrl.includes(environment.apiUrl)
          ) {
            this.currentVideoUrl = URL.createObjectURL(blob);
          }

          this.showRecordedVideo = true;

          const video = this.recordedVideo.nativeElement;

          if (!this.currentVideoUrl.includes(environment.apiUrl)) {
            video.src = this.currentVideoUrl;
            video.onloadedmetadata = () => {
              if (
                video.duration &&
                isFinite(video.duration) &&
                video.duration > 0
              ) {
                video.currentTime = video.duration;
              }
              video.pause();
            };
          }
        });
      }),
    );

    this.subs.push(
      this.recorderService.recordedComment$.subscribe((comment) => {
        if (comment) {
          this.zone.run(() => {
            this.pendingComment = comment;
            this.commentFinalTime = this.commentElapsedTime;
            this.commentRecordingStopped = true;
            this.commentStopRequested = true;
            this.tryFinalizeCommentUpload();
          });
        }
      }),
    );

    this.subs.push(
      this.recorderService.commentTimeRemaining$.subscribe((remaining) => {
        this.zone.run(() => {
          const maxDuration = this.recorderService.getMaxCommentDuration();
          this.commentElapsedTime = maxDuration - remaining;
        });
      }),
    );

    this.subs.push(
      this.recorderService.isVideoOn$.subscribe((v) => (this.isVideoOn = v)),
      this.recorderService.isMicOn$.subscribe((v) => (this.isMicOn = v)),
    );
  }

  toggleCamera() {
    this.recorderService.toggleVideoStream();
  }

  toggleMic() {
    this.recorderService.toggleAudioStream();
  }

  start() {
    if (!this.canRecord || this.isQuestionCompleted) {
      Swal.fire({
        icon: 'info',
        title: 'Question Already Answered',
        text: 'This question has already been answered.',
        confirmButtonText: 'OK',
      });
      return;
    }

    if (this.currentTime >= this.maxTime) {
      this.isTimeUp = true;
      Swal.fire({
        icon: 'warning',
        title: 'Time Limit Reached',
        text: 'The time limit has been reached for this question.',
        confirmButtonText: 'OK',
      });
      return;
    }

    if (!this.isVideoOn || !this.isMicOn) {
      Swal.fire({
        icon: 'warning',
        title: 'Camera & Microphone Required',
        text: 'Please turn on both camera and microphone before starting.',
        confirmButtonText: 'OK',
      });
      return;
    }

    if (this.recordedVideoId) {
      this.showRecordedVideo = false;
      this.startFlag = true;

      this.recorderService.startRecording();
      this.startTimer();

      this.setupChunkUpload();
      return;
    }

    if (!this.sessionId || !this.question.interviewVideo?.videoId) {
      Swal.fire({
        icon: 'error',
        title: 'Recording Error',
        text: 'Unable to start recording. Please refresh and try again.',
        confirmButtonText: 'OK',
      });
      return;
    }

    this.companyVrService
      .initiateRecording(this.sessionId, this.question.interviewVideo.videoId)
      .subscribe({
        next: (recordedVideoId) => {
          const cleanId =
            typeof recordedVideoId === 'string'
              ? recordedVideoId.replace(/"/g, '')
              : recordedVideoId;

          this.recordedVideoId = cleanId;

          this.showRecordedVideo = false;
          this.startFlag = true;

          this.recorderService.startRecording();
          this.startTimer();

          this.setupChunkUpload();
        },
        error: (error) => {
          console.error('Recording initialization failed:', error);
        },
      });
  }

  stop() {
    this.recorderService.pauseRecording();
    this.stopTimer();
    this.showRecordedVideo = true;

    // Disable further recording to make it one-shot
    this.canRecord = false;
    this.isQuestionCompleted = true;

    if (this.question.recordedVideos) {
      this.question.recordedVideos.status = 'RECORDED';
    } else {
      this.question.recordedVideos = { status: 'RECORDED' } as any;
    }

    setTimeout(() => {
      this.currentTime = Math.min(
        this.recorderService.getTotalRecordedDuration(),
        this.maxTime,
      );

      if (!this.question.recordedVideos?.usedAdditional && this.commentsRemaining > 0) {
        this.canUseComment = true;
      }
    }, 150);
  }

  // ─── Comment Recording ───────────────────────────────────────────────────

  getCommentMaxDurationSec(): number {
    return this.question?.additionalAnswerDuration ?? 60;
  }

  ToggleCommentRecoder() {
    if (!this.canUseComment) {
      if (this.question.recordedVideos?.usedAdditional) {
        Swal.fire({ icon: 'info', title: this.translate.instant('interviewUi.commentUsedTitle'), text: this.translate.instant('interviewUi.commentUsedText'), confirmButtonText: this.translate.instant('OK') });
      } else if (!this.isQuestionCompleted) {
        Swal.fire({ icon: 'info', title: this.translate.instant('interviewUi.completeAnswerTitle'), text: this.translate.instant('interviewUi.completeAnswerText'), confirmButtonText: this.translate.instant('OK') });
      } else {
        Swal.fire({ icon: 'info', title: this.translate.instant('interviewUi.commentsUnavailableTitle'), text: this.translate.instant('interviewUi.commentsUnavailableText'), confirmButtonText: this.translate.instant('OK') });
      }
      return;
    }

    const flag = this.commentRecordVisibilty;
    this.commentRecordVisibilty = !flag;

    if (!flag && this.currentCommentNumber === 0) {
      const totalComments = this.question?.remainingAdditionalAttempts ?? 0;
      const usedComments = (this.question?.remainingAdditionalAttempts ?? 0) - this.commentsRemaining;
      this.currentCommentNumber = usedComments + 1;
    }

    if (!flag && this.recorderService.previewStream$.value) {
      setTimeout(() => {
        if (this.commentPreviewVideo) {
          this.commentPreviewVideo.nativeElement.srcObject = this.recorderService.previewStream$.value;
        }
      }, 100);
    }
  }

  startCommentRecording(): void {
    if (!this.canUseComment) {
      if (this.question.recordedVideos?.usedAdditional) {
        Swal.fire({ icon: 'info', title: 'Comment Already Used', text: 'You have already used a comment on this question.', confirmButtonText: 'OK' });
      } else if (!this.isQuestionCompleted) {
        Swal.fire({ icon: 'info', title: 'Complete Main Answer First', text: 'Please finish recording your main answer first.', confirmButtonText: 'OK' });
      } else {
        Swal.fire({ icon: 'info', title: 'Comments Not Available', text: 'Comments are not available for this question.', confirmButtonText: 'OK' });
      }
      return;
    }

    if (!this.isVideoOn || !this.isMicOn) {
      Swal.fire({ icon: 'warning', title: 'Camera & Microphone Required', text: 'Please turn on both camera and microphone before recording your comment.', confirmButtonText: 'OK' });
      return;
    }

    if (this.commentsRemaining <= 0) {
      Swal.fire({ icon: 'warning', title: 'No Comments Remaining', text: 'You have used all available comments for this session.', confirmButtonText: 'OK' });
      return;
    }

    if (this.isRecording$.value) {
      Swal.fire({ icon: 'warning', title: 'Stop Main Recording', text: 'Please stop video recording before adding a comment.', confirmButtonText: 'OK' });
      return;
    }

    if (this.currentQuestionComments.length > 0) {
      Swal.fire({ icon: 'info', title: 'One Comment Per Question', text: 'You can only add one comment per question.', confirmButtonText: 'OK' });
      return;
    }

    if (this.pendingComment) {
      this.pendingComment = null;
      this.commentFinalTime = 0;
    }

    const maxSec = this.getCommentMaxDurationSec();
    this.recorderService.setMaxCommentDuration(maxSec);

    this.isCommentRecording = true;
    this.lastCommentChunkTime = 0;
    this.commentChunkIndex = 0;
    this.commentsRemaining--;
    this.setupCommentChunkUpload();
    this.recorderService.startCommentRecording();
  }

  stopCommentRecording(): void {
    this.commentStopRequested = true;
    this.recorderService.stopCommentRecording();
  }

  private setupCommentChunkUpload() {
    if (!this.commentChunkUploadSubscription && this.recordedVideoId) {
      this.lastCommentChunkTime = 0;
      this.commentChunkIndex = 0;
      this.commentChunkUploadQueue = [];
      this.commentChunkUploadInFlight = false;
      this.commentStopRequested = false;
      this.commentRecordingStopped = false;

      let failedChunks = 0;
      const MAX_FAILED_CHUNKS = 3;

      this.commentChunkUploadSubscription = this.recorderService.commentChunkReady$.subscribe({
        next: (chunk) => {
          if (!this.isCommentRecording && !this.commentStopRequested) return;
          if (!this.recordedVideoId) return;

          const chunkIndex = this.commentChunkIndex;
          this.commentChunkIndex++;

          const maxDurationMs = this.recorderService.getMaxCommentDuration() * 1000;
          let cumulativeDuration = this.recorderService.getCurrentCommentRecordingDuration();

          if (this.commentStopRequested && this.recorderService.commentTimeRemaining$.value <= 0) {
            cumulativeDuration = maxDurationMs;
          }

          cumulativeDuration = Math.min(
            Math.max(cumulativeDuration, this.lastCommentChunkTime),
            maxDurationMs
          );
          this.lastCommentChunkTime = cumulativeDuration;

          this.commentChunkUploadQueue.push({ chunk, cumulativeDuration, chunkIndex });

          this.processNextCommentChunkUpload(
            () => { failedChunks = 0; },
            () => {
              failedChunks++;
              if (failedChunks >= MAX_FAILED_CHUNKS) {
                this.commentChunkUploadSubscription?.unsubscribe();
                this.commentChunkUploadSubscription = null;
              }
            }
          );
        }
      });
    }
  }

  private processNextCommentChunkUpload(onSuccess?: () => void, onFailure?: () => void) {
    if (this.commentChunkUploadInFlight || !this.recordedVideoId) return;

    const nextItem = this.commentChunkUploadQueue.shift();
    if (!nextItem) {
      this.tryFinalizeCommentUpload();
      return;
    }

    this.commentChunkUploadInFlight = true;

    this.companyVrService.appendAdditionalChunk(
      this.recordedVideoId,
      nextItem.chunk,
      nextItem.cumulativeDuration,
      nextItem.chunkIndex
    ).subscribe({
      next: () => {
        this.commentChunkUploadInFlight = false;
        onSuccess?.();
        this.processNextCommentChunkUpload(onSuccess, onFailure);
      },
      error: (error) => {
        this.commentChunkUploadInFlight = false;
        console.error('Comment chunk upload failed:', error);
        onFailure?.();
        this.processNextCommentChunkUpload(onSuccess, onFailure);
      }
    });
  }

  private tryFinalizeCommentUpload(): void {
    if (!this.commentStopRequested || !this.commentRecordingStopped) return;
    if (this.commentChunkUploadInFlight || this.commentChunkUploadQueue.length > 0) return;

    if (this.pendingComment) {
      this.addCommentAutomatically();
    }

    this.isCommentRecording = false;
    this.lastCommentChunkTime = 0;
    this.commentChunkIndex = 0;
    this.commentStopRequested = false;
    this.commentRecordingStopped = false;

    if (this.commentChunkUploadSubscription) {
      this.commentChunkUploadSubscription.unsubscribe();
      this.commentChunkUploadSubscription = null;
    }
  }

  private addCommentAutomatically(): void {
    if (this.pendingComment) {
      this.currentQuestionComments = [...this.currentQuestionComments, this.pendingComment];

      if (this.question.recordedVideos && this.recordedVideoId) {
        this.question.recordedVideos.usedAdditional = true;
        this.canUseComment = false;
      }

      this.pendingComment = null;
      this.commentRecordVisibilty = false;
      this.isCommentRecording = false;
      this.commentAdded.emit();

      Swal.fire({
        icon: 'success',
        title: 'Comment Added',
        text: `Your comment has been added! You have ${this.commentsRemaining} comment${this.commentsRemaining !== 1 ? 's' : ''} remaining.`,
        timer: 2000,
        showConfirmButton: false
      });
    }
  }

  formatCommentTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  getCommentLabel(): string {
    if (this.currentCommentNumber === 1) return this.translate.instant('interviewUi.commentFirst');
    if (this.currentCommentNumber === 2) return this.translate.instant('interviewUi.commentSecond');
    if (this.currentCommentNumber === 3) return this.translate.instant('interviewUi.commentThird');
    return this.translate.instant('interviewUi.commentExtra');
  }

  private updateCommentWaveform(volume: number) {
    this.zone.run(() => {
      const normalized = Math.min(volume / 10, 1);
      for (let i = 0; i < this.commentBars.length - 1; i++) {
        this.commentBars[i] = this.commentBars[i + 1];
      }
      const height = 4 + normalized * 40 * (Math.random() * 0.3 + 0.85);
      this.commentBars[this.commentBars.length - 1] = Math.max(4, height);
    });
  }

  private resetCommentWaveform() {
    this.commentBars.fill(4);
  }

  // ─────────────────────────────────────────────────────────────────────────

  private setupChunkUpload() {
    if (!this.chunkUploadSubscription) {
      let chunkIndex = 0;
      let failedChunks = 0;
      const MAX_FAILED_CHUNKS = 3;

      this.chunkUploadSubscription = this.recorderService.chunkReady$.subscribe(
        {
          next: (chunk) => {
            if (this.recordedVideoId && this.isRecording$.value) {
              const currentChunkIndex = chunkIndex++;

              const cumulativeDuration = Math.min(
                this.recorderService.getTotalRecordedDuration(),
                this.maxTime,
              );

              this.lastChunkTime = cumulativeDuration;
              this.companyVrService
                .appendChunk(
                  this.recordedVideoId,
                  chunk,
                  cumulativeDuration,
                  currentChunkIndex,
                )
                .subscribe({
                  next: () => {
                    failedChunks = 0;
                  },
                  error: (error) => {
                    console.error('Chunk upload failed:', error);
                    failedChunks++;
                    if (failedChunks >= MAX_FAILED_CHUNKS) {
                      this.chunkUploadSubscription?.unsubscribe();
                      this.chunkUploadSubscription = null;
                    }
                  },
                });
            }
          },
        },
      );
    }
  }

  playQuestionAudio() {
    if (this.questionAudio && this.questionAudioUrl) {
      this.interviewService.getSecureAudio(this.questionAudioUrl).subscribe({
        next: (blobUrl) => {
          if (blobUrl) {
            const audio = this.questionAudio.nativeElement;
            audio.src = blobUrl;

            audio
              .play()
              .then(() => {
                this.isPlayingQuestionAudio = true;
              })
              .catch((error) => {
                this.tryDirectQuestionAudioPlay();
              });

            audio.onended = () => {
              this.isPlayingQuestionAudio = false;
              URL.revokeObjectURL(blobUrl);
            };
          } else {
            this.tryDirectQuestionAudioPlay();
          }
        },
        error: (error) => {
          console.error('Audio fetch failed:', error);
          this.tryDirectQuestionAudioPlay();
        },
      });
    }
  }

  private tryDirectQuestionAudioPlay() {
    const audio = this.questionAudio.nativeElement;
    audio.src = this.questionAudioUrl;

    audio
      .play()
      .then(() => {
        this.isPlayingQuestionAudio = true;
      })
      .catch((error) => {
        console.error('Direct audio play failed:', error);
      });

    audio.onended = () => {
      this.isPlayingQuestionAudio = false;
    };
  }

  getQuestionExplanation() {
    if (!this.question.interviewVideo?.videoId || !this.sessionId) {
      return;
    }

    this.isLoadingExplanation = true;

    this.companyVrService
      .explainQuestion(this.sessionId, this.question.interviewVideo.videoId)
      .subscribe({
        next: (response: any) => {
          this.zone.run(() => {
            this.explainText = response.explainText;
            const fixedUrl = response.explainUrl
              ? response.explainUrl.replace(/\\/g, '/')
              : '';
            this.explainAudioUrl = fixedUrl
              ? environment.apiUrl + fixedUrl
              : '';
            this.showExplanation = true;
            this.hasExplanation = false;
            this.activeQuestion = this.explainText;
            this.isLoadingExplanation = false;

            if (this.explainAudioUrl) {
              setTimeout(() => {
                this.playExplanationAudio();
              }, 100);
            }
          });
        },
        error: (error) => {
          console.error('Explanation fetch failed:', error);
          this.isLoadingExplanation = false;
        },
      });
  }

  playExplanationAudio() {
    if (this.explainAudio && this.explainAudioUrl) {
      this.interviewService.getSecureAudio(this.explainAudioUrl).subscribe({
        next: (blobUrl) => {
          if (blobUrl) {
            const audio = this.explainAudio.nativeElement;
            audio.src = blobUrl;

            audio
              .play()
              .then(() => {
                this.isPlayingExplanation = true;
              })
              .catch((error) => {
                this.tryDirectAudioPlay();
              });

            audio.onended = () => {
              this.isPlayingExplanation = false;
              URL.revokeObjectURL(blobUrl);
            };
          } else {
            this.tryDirectAudioPlay();
          }
        },
        error: (error) => {
          console.error('Explanation audio fetch failed:', error);
          this.tryDirectAudioPlay();
        },
      });
    }
  }

  private tryDirectAudioPlay() {
    const audio = this.explainAudio.nativeElement;
    audio.src = this.explainAudioUrl;

    audio
      .play()
      .then(() => {
        this.isPlayingExplanation = true;
      })
      .catch((error) => {
        console.error('Direct explanation audio play failed:', error);
      });

    audio.onended = () => {
      this.isPlayingExplanation = false;
    };
  }

  onNext() {
    if (!this.startFlag) {
      Swal.fire({
        icon: 'warning',
        title: 'Recording Required',
        text: 'Please record your answer before proceeding.',
        confirmButtonText: 'OK',
      });
      return;
    }

    if (this.isRecording$.value) {
      Swal.fire({
        icon: 'warning',
        title: 'Stop Recording First',
        text: 'Please stop the recording before moving to the next question.',
        confirmButtonText: 'OK',
      });
      return;
    }

    if (this.recordedVideoId) {
      this.finishRecording().subscribe({
        next: () => {
          this.proceedToNext();
        },
        error: (error: any) => {
          console.error('Finish API failed:', error);
          this.proceedToNext();
        },
      });
    } else {
      this.proceedToNext();
    }
  }

  private proceedToNext() {
    const answerData = {
      duration: Math.floor(this.currentTime / 1000),
      videoBlob: this.currentVideoBlob,
      videoUrl: this.currentVideoUrl,
      recordedVideoId: this.recordedVideoId,
    };

    this.next.emit(answerData);
    this.resetComponent();
  }

  private finishRecording(): Observable<any> {
    if (!this.recordedVideoId) {
      return of(null);
    }

    return this.companyVrService.finishRecording(this.recordedVideoId);
  }

  onSubmit(): boolean {
    return this.isLastQuestion;
  }

  private startTimer() {
    if (this.speakingTimer) return;

    this.speakingTimer = setInterval(() => {
      const totalDuration = this.recorderService.getTotalRecordedDuration();
      this.currentTime = Math.min(totalDuration, this.maxTime);

      if (this.currentTime >= this.maxTime && !this.isTimeUp) {
        this.zone.run(() => {
          this.currentTime = this.maxTime;
          this.isTimeUp = true;
          this.stopRecordingGracefully();
        });
      }
    }, 250);
  }

  private stopRecordingGracefully() {
    const waitForFinalBlob = new Promise<void>((resolve) => {
      const sub = this.recorderService.recordedBlob$.subscribe((blob) => {
        if (blob) {
          sub.unsubscribe();

          setTimeout(() => {
            resolve();
          }, 3000);
        }
      });

      this.recorderService.stopRecording();

      setTimeout(() => {
        sub.unsubscribe();
        resolve();
      }, 5000);
    });

    waitForFinalBlob.then(() => {
      if (this.chunkUploadSubscription) {
        this.chunkUploadSubscription.unsubscribe();
        this.chunkUploadSubscription = null;
      }

      this.stopTimer();
      Swal.fire({
        icon: 'info',
        title: 'Time is Up',
        text: 'Recording completed. The time limit has been reached.',
        confirmButtonText: 'OK',
      });
    });
  }

  private stopTimer() {
    if (this.speakingTimer) {
      clearInterval(this.speakingTimer);
      this.speakingTimer = null;
    }
  }

  private updateWaveform(volume: number) {
    const normalized = Math.min(volume / 10, 1);
    for (let i = 0; i < this.bars.length; i++) {
      this.bars[i] = Math.max(4, Math.random() * normalized * 40);
    }
  }

  private resetWaveform() {
    this.bars.fill(4);
  }

  freezeLastFrame() {
    if (!this.recordedVideo) return;
    const video = this.recordedVideo.nativeElement;
    video.currentTime = Math.max(video.duration - 0.05, 0);
    video.pause();
  }

  formatDuration(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }

  private resetComponent() {
    this.currentTime = 0;
    this.startFlag = false;
    this.showRecordedVideo = false;
    this.isTimeUp = false;
    this.currentVideoBlob = null;
    this.recordedVideoId = null;
    this.lastChunkTime = 0;
    this.canRecord = true;
    this.isQuestionCompleted = false;

    if (this.chunkUploadSubscription) {
      this.chunkUploadSubscription.unsubscribe();
      this.chunkUploadSubscription = null;
    }

    if (
      this.currentVideoUrl &&
      !this.currentVideoUrl.includes(environment.apiUrl)
    ) {
      URL.revokeObjectURL(this.currentVideoUrl);
    }
    this.currentVideoUrl = null;

    this.recorderService.clearRecording();
    this.recorderService.resetTotalDuration();
    this.recorderService.closeMicAndCamera();

    this.resetWaveform();
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
    this.stopTimer();

    if (this.chunkUploadSubscription) {
      this.chunkUploadSubscription.unsubscribe();
      this.chunkUploadSubscription = null;
    }

    if (
      this.currentVideoUrl &&
      !this.currentVideoUrl.includes(environment.apiUrl)
    ) {
      URL.revokeObjectURL(this.currentVideoUrl);
    }
    this.currentVideoUrl = null;

    this.recorderService.resetMaxCommentDuration();
    this.recorderService.finalizeAndCleanup();
  }
}
